import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportFilterBar from "./ReportFilterBar";
import ReportTable from "./ReportTable";
import Pagination from "../Pagination/Pagination";
import { exportTaxReport, getTaxReport } from "../../api/reports";
import { formatINR, formatPercent } from "./reportUtils";

const formatTaxDate = (value) => {
  if (!value) return "—";

  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00+05:30`
      : value,
  );
  if (Number.isNaN(date.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
};

const TaxReport = () => {
  const [, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    q: "",
    page: 1,
    limit: 10,
    sortBy: "date",
    sortOrder: "desc",
    state: "",
    gstRate: "",
    type: "invoice",
  });

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [reportTotals, setReportTotals] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchTaxReport = async () => {
      setLoading(true);
      setError("");
      try {
        const { q, ...queryFilters } = filters;
        const response = await getTaxReport({
          ...queryFilters,
          search: q,
        });
        if (cancelled) return;

        const reportRows = response?.data?.rows || response?.rows || [];
        setRows(reportRows);
        setReportTotals(response?.data?.totals || response?.totals || {});
        setPagination(
          response?.pagination || {
            page: filters.page,
            limit: filters.limit,
            total: reportRows.length,
            totalPages: 1,
          },
        );
      } catch (fetchError) {
        if (cancelled) return;
        setRows([]);
        setReportTotals({});
        setPagination({ page: 1, limit: filters.limit, total: 0, totalPages: 1 });
        setError(
          fetchError?.response?.data?.message || fetchError.message || "Failed to load tax report.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTaxReport();
    return () => {
      cancelled = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.q,
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.state,
    filters.gstRate,
    filters.type,
  ]);

  const totals = {
    taxableValue: {
      value: reportTotals.taxableValue || 0,
      format: "inr",
    },
    cgst: {
      value: reportTotals.cgst || 0,
      format: "inr",
    },
    sgst: {
      value: reportTotals.sgst || 0,
      format: "inr",
    },
    igst: {
      value: reportTotals.igst || 0,
      format: "inr",
    },
    total: {
      value: reportTotals.total || 0,
      format: "inr",
    },
  };

  const columns = [
    {
      key: "orderId",
      label: "Order ID",
      sortable: true,
      minWidth: 180,
      render: (row) => (
        <div>
          <div>{row.orderId}</div>
        </div>
      ),
    },
    {
      key: "date",
      label: "Date",
      sortable: true,
      render: (row) => formatTaxDate(row.date),
    },
    {
      key: "customerState",
      label: "Customer State",
      sortable: true,
    },
    {
      key: "taxableValue",
      label: "Taxable Value",
      sortable: true,
      render: (row) => formatINR(row.taxableValue),
    },
    {
      key: "gstRate",
      label: "GST %",
      sortable: true,
      render: (row) => formatPercent(row.gstRate),
    },
    {
      key: "cgst",
      label: "CGST",
      sortable: true,
      render: (row) => formatINR(row.cgst),
    },
    {
      key: "sgst",
      label: "SGST",
      sortable: true,
      render: (row) => formatINR(row.sgst),
    },
  ];

  const handleExport = async () => {
    setExporting(true);
    setError("");

    try {
      const response = await exportTaxReport({
        from: filters.from,
        to: filters.to,
        state: filters.state,
        gstRate: filters.gstRate,
        type: filters.type,
        search: filters.q,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });
      const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = response.headers?.["content-disposition"];
      const fileNameMatch = disposition?.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
      const fileName = fileNameMatch?.[1]
        ? decodeURIComponent(fileNameMatch[1].replace(/"/g, ""))
        : "tax-report.csv";

      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(
        exportError?.response?.data?.message ||
          exportError.message ||
          "Failed to export tax report.",
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="container-fluid p-4 report-page">
      <div className="card p-3">
        <ReportFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={setFilters}
          searchPlaceholder="Search order, invoice or customer..."
          filterSlotInline
          actionSlot={
            <button
              type="button"
              className="btn btn-success w-100"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? "Exporting..." : "Export CSV"}
            </button>
          }
          filterSlot={
            <>
              <div className="col-12 col-md-4 col-lg-2">
                <label htmlFor="gst-state" className="form-label">
                  State
                </label>

                <select
                  id="gst-state"
                  className="form-select"
                  value={filters.state}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      state: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <option value="">All States</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                  <option value="Assam">Assam</option>
                  <option value="Bihar">Bihar</option>
                  <option value="Chhattisgarh">Chhattisgarh</option>
                  <option value="Goa">Goa</option>
                  <option value="Gujarat">Gujarat</option>
                  <option value="Haryana">Haryana</option>
                  <option value="Himachal Pradesh">Himachal Pradesh</option>
                  <option value="Jharkhand">Jharkhand</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Kerala">Kerala</option>
                  <option value="Madhya Pradesh">Madhya Pradesh</option>
                  <option value="Maharashtra">Maharashtra</option>
                  <option value="Manipur">Manipur</option>
                  <option value="Meghalaya">Meghalaya</option>
                  <option value="Mizoram">Mizoram</option>
                  <option value="Nagaland">Nagaland</option>
                  <option value="Odisha">Odisha</option>
                  <option value="Punjab">Punjab</option>
                  <option value="Rajasthan">Rajasthan</option>
                  <option value="Sikkim">Sikkim</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Telangana">Telangana</option>
                  <option value="Tripura">Tripura</option>
                  <option value="Uttar Pradesh">Uttar Pradesh</option>
                  <option value="Uttarakhand">Uttarakhand</option>
                  <option value="West Bengal">West Bengal</option>
                </select>
              </div>
            </>
          }
        />

        <ReportTable
          columns={columns}
          rows={rows}
          totals={totals}
          loading={loading}
          error={error}
          page={filters.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={(sortBy, sortOrder) =>
            setFilters((prev) => ({
              ...prev,
              sortBy,
              sortOrder,
              page: 1,
            }))
          }
          onPageChange={(page) =>
            setFilters((prev) => ({
              ...prev,
              page,
            }))
          }
          rowKey="invoiceNo"
        />
      </div>
      <Pagination
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        currentPage={pagination.page}
        onPageChange={(page) =>
          setFilters((prev) => ({
            ...prev,
            page,
          }))
        }
      />
    </div>
  );
};

export default TaxReport;
